import { useEffect, useState } from 'react'
import { useAppStore } from '../../shared/store/store'
import { initializeGTM } from '../analytics/initialize-gtm'
import { produceGtmEvent } from '../analytics/produce-gtm-event'
import {
  ViewerAverageFpsMetricEvent,
  ViewerGTMEvents,
  ViewerInitedEvent,
  ViewerLoadedEvent,
  ViewerMaterialColorChangedEvent,
  ViewerDiamondCutChangedEvent,
  ViewerPointOfViewChangedEvent,
  ViewerTimeStampEvent,
} from '../analytics/viewer-gtm-events'
import { PointOfView } from '../../../../trillion-common/src/objects/point-of-view.object'
import Stats from '@trillionhq/stats.js'
import { dispatchViewerIsLoaded } from '../utils/dispatch-loaded'

const TRACK_AVG_FPS_AFTER = 3 * 1000
const SEND_AVG_FPS_TO_GTM_AFTER = 8 * 1000

export function useGoogleTagManager(jewelryId: string, stats: Stats | undefined) {
  const isLoading = useAppStore((state) => state.isLoading)
  const pointsOfView = useAppStore((state) => state.pointsOfView)
  const currentPointOfViewIndx = useAppStore((state) => state.currentPointOfViewIndx)
  const currentMaterialColor = useAppStore((state) => state.currentMaterialColor)
  const currentDiamondCut = useAppStore((state) => state.currentDiamondCut)

  const [isViewerInited, setIsViewerInited] = useState<boolean>(false)
  const [isViewerLoaded, setIsViewerLoaded] = useState<boolean>(false)
  const [initializationTime, setInitializationTime] = useState<number>()
  const [lastPov, setLastPov] = useState<PointOfView | undefined>(undefined)

  // initedEvent
  useEffect(() => {
    const initialized = initializeGTM()
    const dateTimeString = new Date().toISOString()

    if (initialized) {
      setIsViewerInited(true)
      produceGtmEvent<ViewerInitedEvent>({ event: ViewerGTMEvents.initedEvent, jewelryId }, true)
      produceGtmEvent<ViewerTimeStampEvent>(
        { event: ViewerGTMEvents.timestampEvent, dateTimeString },
        true,
      )
      setInitializationTime(new Date().getTime())
    }
  }, [])

  // loadedEvent
  useEffect(() => {
    if (!isLoading && isViewerInited && !isViewerLoaded && initializationTime) {
      const loadingTime = ((new Date().getTime() - initializationTime) / 1000).toFixed(1)
      console.log(
        `Trillion 3D Viewer ${
          import.meta.env.VITE_IS_LIBRARY_MODE ? __APP_VERSION__ : 'DEV'
        } started.\nLoad time: ${loadingTime}s`,
      )
      dispatchViewerIsLoaded()
      produceGtmEvent<ViewerLoadedEvent>(
        {
          event: ViewerGTMEvents.loadedEvent,
          loadTimeSec: +loadingTime,
        },
        true,
      )
      setIsViewerLoaded(true)
    }
  }, [isLoading, isViewerInited, isViewerLoaded, initializationTime])

  // materialColorChangedEvent
  useEffect(() => {
    if (currentMaterialColor && isViewerLoaded) {
      produceGtmEvent<ViewerMaterialColorChangedEvent>({
        event: ViewerGTMEvents.materialColorChangedEvent,
        materialColorDescription: currentMaterialColor.description,
      })
    }
  }, [currentMaterialColor, isViewerLoaded])

  // diamondCutChangedEvent
  useEffect(() => {
    if (currentDiamondCut && isViewerLoaded) {
      produceGtmEvent<ViewerDiamondCutChangedEvent>({
        event: ViewerGTMEvents.diamondCutChangedEvent,
        diamondCutDescription: currentDiamondCut.description,
      })
    }
  }, [currentDiamondCut, isViewerLoaded])

  // pointOfViewChangedEvent
  useEffect(() => {
    if (pointsOfView.length > 0 && currentPointOfViewIndx > -1 && isViewerLoaded) {
      const currPov = pointsOfView[currentPointOfViewIndx]
      setLastPov(currPov)
    }
  }, [pointsOfView, currentPointOfViewIndx, isViewerLoaded])

  useEffect(() => {
    if (lastPov) {
      produceGtmEvent<ViewerPointOfViewChangedEvent>({
        event: ViewerGTMEvents.pointOfViewChangedEvent,
        povDescription: lastPov.description,
      })
    }
  }, [lastPov])

  // averageFpsMetricEvent
  useEffect(() => {
    if (isLoading || !stats) return

    const trackTimeout = setTimeout(() => {
      if (stats) {
        stats.startTrackAverageFps()
      }
    }, TRACK_AVG_FPS_AFTER)

    const sendTimeout = setTimeout(() => {
      if (stats) {
        const avgFps = stats.getGeneralAverageFPS()
        produceGtmEvent<ViewerAverageFpsMetricEvent>({
          event: ViewerGTMEvents.averageFpsMetricEvent,
          averageFps: avgFps,
        })
      }
    }, SEND_AVG_FPS_TO_GTM_AFTER)

    return () => {
      clearTimeout(trackTimeout)
      clearTimeout(sendTimeout)
    }
  }, [isLoading, stats])
}
